// src/pages/Settings.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../config/api";
import { get, post, put, del } from "../lib/http";

/* ----------------------- tiny utils ----------------------- */
const cn = (...a) => a.filter(Boolean).join(" ");
const cap = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);
const formatYMD = (v) => {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const ACCEPTED_IMG = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMG_MB = 5;

/* ----------------------- page ----------------------------- */
export default function Settings() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [tabMsg, setTabMsg] = useState({}); // {profile:'', security:'', ...}

  const [profile, setProfile] = useState({
    name: "",
    gender: "",
    dob: "",
    profileImage: "",
  });

  const [account, setAccount] = useState({
    email: "",
    mobile: "",
  });

  const [security, setSecurity] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFA: false,
  });

  const [notify, setNotify] = useState({
    email: true,
    sms: false,
    whatsapp: false,
  });

  const [prefs, setPrefs] = useState({
    language: "English",
    theme: "system", // system | light | dark
  });

  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  /* ----------------------- load me ------------------------ */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setMsg("");
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

        const meUrl =
          (API?.user?.me && typeof API.user.me === "function")
            ? API.user.me()
            : (API?.user?.me || "/api/auth/me");

        const res = await get(meUrl, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });

        const u = res?.user || res?.data?.user || res;

        setProfile({
          name: u?.name || "",
          gender: u?.gender || "",
          dob: formatYMD(u?.dob),
          profileImage: u?.profileImage || "",
        });
        setAccount({
          email: u?.email || "",
          mobile: u?.mobile || "",
        });
        setSecurity((s) => ({ ...s, twoFA: !!u?.twoFA }));
        setNotify({
          email: !!(u?.notifications?.email ?? true),
          sms: !!(u?.notifications?.sms ?? false),
          whatsapp: !!(u?.notifications?.whatsapp ?? false),
        });
        setPrefs({
          language: u?.preferences?.language || "English",
          theme: u?.preferences?.theme || "system",
        });
      } catch (e) {
        console.error(e);
        setMsg(e?.message || "Failed to load settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ----------------------- handlers ----------------------- */
  const onProf = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const onAcc = (k, v) => setAccount((p) => ({ ...p, [k]: v }));
  const onSec = (k, v) => setSecurity((p) => ({ ...p, [k]: v }));
  const onNotify = (k, v) => setNotify((p) => ({ ...p, [k]: v }));
  const onPrefs = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const setSectionMsg = (key, text) => setTabMsg((m) => ({ ...m, [key]: text }));

  /* ----------------------- upload avatar ------------------ */
  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_IMG.includes(file.type)) {
      return setSectionMsg("profile", "Please upload JPG/PNG/WEBP.");
    }
    if (file.size > MAX_IMG_MB * 1024 * 1024) {
      return setSectionMsg("profile", `Max ${MAX_IMG_MB} MB allowed.`);
    }

    try {
      setUploading(true);
      setUploadPct(10);

      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const fd = new FormData();
      fd.append("file", file);

      const url =
        (API?.user?.uploadAvatar && typeof API.user.uploadAvatar === "string")
          ? API.user.uploadAvatar
          : (API?.user?.uploadAvatar?.url || "/api/user/upload-avatar");

      const resp = await post(url, fd, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (pe) => {
          if (!pe?.total) return;
          const pct = Math.round((pe.loaded / pe.total) * 100);
          setUploadPct(Math.max(15, pct));
        },
      });

      const link =
        resp?.url ||
        resp?.fileUrl ||
        resp?.secure_url ||
        resp?.location ||
        resp?.data?.url ||
        "";

      if (!link) throw new Error("Avatar uploaded but URL missing in response.");
      onProf("profileImage", link);
      setSectionMsg("profile", "✅ Avatar updated.");
    } catch (e2) {
      console.error(e2);
      setSectionMsg("profile", e2?.message || "Avatar upload failed.");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  /* ----------------------- save profile ------------------- */
  const saveProfile = async () => {
    try {
      setSectionMsg("profile", "");
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const url =
        (API?.user?.updateProfile && typeof API.user.updateProfile === "string")
          ? API.user.updateProfile
          : (API?.user?.updateProfile?.url || "/api/user/profile");

      await put(url, { ...profile }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setSectionMsg("profile", "✅ Profile saved.");
    } catch (e) {
      console.error(e);
      setSectionMsg("profile", e?.message || "Failed to save profile.");
    }
  };

  /* ----------------------- save account ------------------- */
  const saveAccount = async () => {
    try {
      setSectionMsg("account", "");
      if (account.email && !/^\S+@\S+\.\S+$/.test(account.email)) {
        return setSectionMsg("account", "Enter a valid email.");
      }
      if (account.mobile && !/^\+?\d[\d\- ]{6,}$/.test(account.mobile)) {
        return setSectionMsg("account", "Enter a valid phone.");
      }

      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.updateAccount && typeof API.user.updateAccount === "string")
          ? API.user.updateAccount
          : (API?.user?.updateAccount?.url || "/api/user/account");

      await put(url, account, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setSectionMsg("account", "✅ Account saved.");
    } catch (e) {
      console.error(e);
      setSectionMsg("account", e?.message || "Failed to save account.");
    }
  };

  /* ----------------------- change password ---------------- */
  const changePassword = async () => {
    try {
      setSectionMsg("security", "");
      if (!security.currentPassword || !security.newPassword || !security.confirmPassword) {
        return setSectionMsg("security", "All password fields are required.");
      }
      if (security.newPassword !== security.confirmPassword) {
        return setSectionMsg("security", "New passwords do not match.");
      }
      if (security.newPassword.length < 6) {
        return setSectionMsg("security", "New password must be 6+ chars.");
      }

      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.changePassword && typeof API.user.changePassword === "string")
          ? API.user.changePassword
          : (API?.user?.changePassword?.url || "/api/user/change-password");

      await post(url, {
        currentPassword: security.currentPassword,
        newPassword: security.newPassword,
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      setSectionMsg("security", "✅ Password changed.");
      setSecurity((s) => ({ ...s, currentPassword: "", newPassword: "", confirmPassword: "" }));
    } catch (e) {
      console.error(e);
      setSectionMsg("security", e?.message || "Failed to change password.");
    }
  };

  /* ----------------------- toggle 2FA --------------------- */
  const toggle2FA = async (val) => {
    try {
      onSec("twoFA", val);
      setSectionMsg("security", "");
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.toggle2FA && typeof API.user.toggle2FA === "string")
          ? API.user.toggle2FA
          : (API?.user?.toggle2FA?.url || "/api/user/twofa");

      await post(url, { enabled: val }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setSectionMsg("security", `✅ 2FA ${val ? "enabled" : "disabled"}.`);
    } catch (e) {
      console.error(e);
      setSectionMsg("security", e?.message || "Failed to update 2FA.");
      onSec("twoFA", !val); // revert
    }
  };

  /* ----------------------- notifications ------------------ */
  const saveNotify = async () => {
    try {
      setSectionMsg("notifications", "");
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.updateNotifications && typeof API.user.updateNotifications === "string")
          ? API.user.updateNotifications
          : (API?.user?.updateNotifications?.url || "/api/user/notifications");

      await put(url, notify, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setSectionMsg("notifications", "✅ Notification preferences saved.");
    } catch (e) {
      console.error(e);
      setSectionMsg("notifications", e?.message || "Failed to save notifications.");
    }
  };

  /* ----------------------- preferences -------------------- */
  const savePrefs = async () => {
    try {
      setSectionMsg("preferences", "");
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.updatePreferences && typeof API.user.updatePreferences === "string")
          ? API.user.updatePreferences
          : (API?.user?.updatePreferences?.url || "/api/user/preferences");

      await put(url, prefs, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setSectionMsg("preferences", "✅ Preferences saved.");
    } catch (e) {
      console.error(e);
      setSectionMsg("preferences", e?.message || "Failed to save preferences.");
    }
  };

  /* ----------------------- danger zone -------------------- */
  const deactivate = async () => {
    if (!window.confirm("Are you sure you want to deactivate your account? You can reactivate by logging in again.")) return;
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.deactivate && typeof API.user.deactivate === "string")
          ? API.user.deactivate
          : (API?.user?.deactivate?.url || "/api/user/deactivate");

      await post(url, {}, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      alert("Account deactivated.");
      nav("/"); // redirect
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to deactivate.");
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Danger! This will permanently delete your account and data. Continue?")) return;
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.user?.delete && typeof API.user.delete === "string")
          ? API.user.delete
          : (API?.user?.delete?.url || "/api/user");

      await del(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      alert("Your account has been deleted.");
      localStorage.removeItem("bb_user");
      nav("/");
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to delete account.");
    }
  };

  /* ----------------------- loading ------------------------ */
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="bg-white shadow-lg rounded-2xl p-4 sm:p-6 animate-pulse">
          <div className="h-7 sm:h-8 w-1/2 sm:w-1/3 bg-gray-200 rounded mb-4 sm:mb-6" />
          <div className="space-y-3 sm:space-y-4">
            <div className="h-5 sm:h-6 w-2/3 bg-gray-200 rounded" />
            <div className="h-5 sm:h-6 w-3/4 bg-gray-200 rounded" />
            <div className="h-5 sm:h-6 w-1/3 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  /* ----------------------- UI ----------------------------- */
  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
      {/* header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav(-1)}
            className="rounded-full border px-3 py-2 text-sm font-semibold hover:bg-amber-50"
            aria-label="Back"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900">Settings</h1>
            <p className="text-xs sm:text-sm text-slate-600">
              Manage your profile, security, notifications and preferences.
            </p>
          </div>
        </div>

        {msg && <div className="text-sm text-rose-600">{msg}</div>}
      </div>

      {/* Profile */}
      <Card title="Profile" subtitle="Personal details visible to you">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
          <div className="shrink-0">
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl overflow-hidden border bg-slate-50 grid place-items-center">
              {profile.profileImage ? (
                <img src={profile.profileImage} alt="avatar" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xl sm:text-2xl text-slate-400">👤</span>
              )}
            </div>
            <div className="mt-3">
              <input
                id="avatar-file"
                type="file"
                accept={ACCEPTED_IMG.join(",")}
                className="hidden"
                onChange={onAvatarFile}
              />
              <label
                htmlFor="avatar-file"
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold hover:bg-slate-50 cursor-pointer"
              >
                {uploading ? "Uploading…" : "Change Avatar"}
              </label>
              {uploading && (
                <div className="mt-2 h-1.5 rounded bg-slate-200 overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${uploadPct}%` }} />
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 w-full">
            <Input label="Name" value={profile.name} onChange={(v) => onProf("name", v)} required />
            <Select label="Gender" value={profile.gender} onChange={(v) => onProf("gender", v)} options={["", "Male", "Female", "Other"]} />
            <Input label="Date of Birth" type="date" value={profile.dob} onChange={(v) => onProf("dob", v)} />
          </div>
        </div>

        {tabMsg.profile && <Note ok={tabMsg.profile.startsWith("✅")}>{tabMsg.profile}</Note>}

        <div className="flex items-center justify-end">
          <Primary onClick={saveProfile}>Save Profile</Primary>
        </div>
      </Card>

      {/* Account */}
      <Card title="Account" subtitle="Email and phone used for login and communication">
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
          <Input label="Email" type="email" value={account.email} onChange={(v) => onAcc("email", v)} placeholder="you@example.com" />
          <Input label="Mobile" value={account.mobile} onChange={(v) => onAcc("mobile", v)} placeholder="+91 98xxxxxxx" />
        </div>

        {tabMsg.account && <Note ok={tabMsg.account.startsWith("✅")}>{tabMsg.account}</Note>}

        <div className="flex items-center justify-end">
          <Primary onClick={saveAccount}>Save Account</Primary>
        </div>
      </Card>

      {/* Security */}
      <Card title="Security" subtitle="Update password and enable 2-factor authentication">
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
          <Input label="Current Password" type="password" value={security.currentPassword} onChange={(v) => onSec("currentPassword", v)} />
          <Input label="New Password" type="password" value={security.newPassword} onChange={(v) => onSec("newPassword", v)} />
          <Input label="Confirm Password" type="password" value={security.confirmPassword} onChange={(v) => onSec("confirmPassword", v)} />
        </div>

        <div className="mt-3">
          <Toggle label="Enable 2FA (OTP)" checked={security.twoFA} onChange={toggle2FA} />
        </div>

        {tabMsg.security && <Note ok={tabMsg.security.startsWith("✅")}>{tabMsg.security}</Note>}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
          <button
            onClick={changePassword}
            className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            Change Password
          </button>
        </div>
      </Card>

      {/* Notifications */}
      <Card title="Notifications" subtitle="How we keep you updated">
        <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-3">
          <Toggle label="Email" checked={notify.email} onChange={(v) => onNotify("email", v)} />
          <Toggle label="SMS" checked={notify.sms} onChange={(v) => onNotify("sms", v)} />
          <Toggle label="WhatsApp" checked={notify.whatsapp} onChange={(v) => onNotify("whatsapp", v)} />
        </div>

        {tabMsg.notifications && <Note ok={tabMsg.notifications.startsWith("✅")}>{tabMsg.notifications}</Note>}

        <div className="flex items-center justify-end">
          <Primary onClick={saveNotify}>Save Notification Settings</Primary>
        </div>
      </Card>

      {/* Preferences */}
      <Card title="Preferences" subtitle="Language and appearance">
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
          <Select
            label="Language"
            value={prefs.language}
            onChange={(v) => onPrefs("language", v)}
            options={["English", "हिंदी", "मराठी", "ગુજરાતી", "বাংলা", "தமிழ்", "తెలుగు"]}
          />
          <Select label="Theme" value={prefs.theme} onChange={(v) => onPrefs("theme", v)} options={["system", "light", "dark"]} />
        </div>

        {tabMsg.preferences && <Note ok={tabMsg.preferences.startsWith("✅")}>{tabMsg.preferences}</Note>}

        <div className="flex items-center justify-end">
          <Primary onClick={savePrefs}>Save Preferences</Primary>
        </div>
      </Card>

      {/* Danger zone */}
      <Card title="Danger Zone" subtitle="Irreversible actions — proceed with caution">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4">
          <div>
            <div className="font-semibold text-amber-900">Deactivate Account</div>
            <div className="text-xs sm:text-sm text-amber-800">Temporarily disable your account. You can reactivate later by logging in.</div>
          </div>
          <button
            onClick={deactivate}
            className="rounded-full border border-amber-400 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
          >
            Deactivate
          </button>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3 sm:p-4">
          <div>
            <div className="font-semibold text-rose-700">Delete Account</div>
            <div className="text-xs sm:text-sm text-rose-600">Permanently delete your account and data. This cannot be undone.</div>
          </div>
          <button
            onClick={deleteAccount}
            className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-semibold hover:bg-rose-700"
          >
            Delete Account
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ----------------------- UI bits ------------------------- */
function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-5 space-y-4">
      <div>
        <div className="text-base sm:text-lg font-bold text-slate-900">{title}</div>
        {subtitle && <div className="text-xs sm:text-sm text-slate-500">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
function Label({ children }) {
  return <label className="block text-sm font-semibold text-slate-700">{children}</label>;
}
function Input({ label, value, onChange, placeholder, type = "text", required = false }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-rose-600"> *</span>}</Label>
      <input
        type={type}
        className="mt-1 w-full rounded-xl border bg-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-amber-300"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
}
function Select({ label, value, onChange, options = [] }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <select
          className="mt-1 w-full rounded-xl border bg-white px-3 py-2.5 pr-9 outline-none focus:ring-2 focus:ring-amber-300"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o} value={o}>{cap(o)}</option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </div>
  );
}
function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2 bg-white">
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 items-center rounded-full transition",
          checked ? "bg-emerald-500" : "bg-slate-300"
        )}
        aria-pressed={checked}
      >
        <span
          className={cn(
            "inline-block h-5 w-5 transform rounded-full bg-white transition",
            checked ? "translate-x-5" : "translate-x-1"
          )}
        />
      </button>
    </label>
  );
}
function Primary({ children, className = "", ...props }) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2 font-semibold text-white shadow-sm hover:bg-amber-600 w-full sm:w-auto",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
function Note({ children, ok = false }) {
  return (
    <div className={cn(
      "rounded-xl border px-3 py-2 text-sm",
      ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"
    )}>
      {children}
    </div>
  );
}
